import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Loader2, Download, Users, Briefcase, CalendarRange, Package } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { API_URL, downloadExport, fmtDT } from '../components/leadmgmt/lmShared';
import { LmLeadsSheet } from '../components/leadmgmt/LmLeadsSheet';
import { LmBatchesCard } from '../components/leadmgmt/LmBatchesCard';

const KPIS = [
  { key: 'total', label: 'Total', cls: 'text-slate-800' },
  { key: 'por_atribuir', label: 'Por atribuir', cls: 'text-amber-600' },
  { key: 'parada', label: 'Paradas +24h', cls: 'text-rose-600' },
  { key: 'a_expirar', label: 'A expirar (<6h)', cls: 'text-orange-500' },
  { key: 'expirada', label: 'Expiradas 42h', cls: 'text-rose-700' },
  { key: 'atrasadas', sla: 'atrasada', label: 'Follow-up em atraso', cls: 'text-rose-600' },
  { key: 'aguarda_contacto', label: 'Aguarda 1º contacto', cls: 'text-slate-600' },
  { key: 'em_curso', label: 'Em curso (42h)', cls: 'text-sky-700' },
  { key: 'convertida', label: 'Convertidas (contrato)', cls: 'text-emerald-600', big: true },
  { key: 'fechada_sem_contrato', label: 'Fechadas sem contrato', cls: 'text-slate-500', big: true },
];

const FilterSelect = ({ icon: Icon, label, value, onChange, options, testid }) => (
  <div>
    <label className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export default function LeadManagementPage() {
  const { user } = useAuth();
  if (user && !['super_admin', 'gestor'].includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <LeadManagementInner />;
}

function LeadManagementInner() {
  const [partner, setPartner] = useState('all');
  const [comercial, setComercial] = useState('all');
  const [period, setPeriod] = useState('all');
  const [batch, setBatch] = useState('all');
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [sheet, setSheet] = useState(null);

  const filters = useMemo(() => ({
    partner_id: partner !== 'all' ? partner : undefined,
    comercial_id: comercial !== 'all' ? comercial : undefined,
    period: period !== 'all' ? period : undefined,
    batch: batch !== 'all' ? batch : undefined,
  }), [partner, comercial, period, batch]);

  const load = useCallback(() => {
    axios.get(`${API_URL}/lead-mgmt/overview`, { params: filters }).then((r) => setData(r.data)).catch(() => setData({ kpis: {}, comerciais: [], batches: [] }));
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API_URL}/leads/assignable-users`).then((r) => setUsers((r.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt')))).catch(() => {});
    axios.get(`${API_URL}/leads/assignable-partners`).then((r) => setPartners(r.data || [])).catch(() => {});
  }, []);

  const usersOfPartner = partner === 'all' ? users : users.filter((u) => u.partner_id === partner);
  const k = data?.kpis || {};
  const open = (title, extra = {}, sla = '') => setSheet({ title, filters: { ...filters, ...extra }, sla });

  return (
    <Layout>
      <div className="space-y-6" data-testid="lead-management-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Gestão de Leads</h1>
            <p className="text-sm text-slate-500">O que está a acontecer às leads que compra e entrega à equipa: 24h para o 1º contacto, 42h para fechar.</p>
          </div>
          <button type="button" onClick={() => downloadExport(axios, { kind: 'comerciais', ...filters })} className="text-sm px-4 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1.5" data-testid="lm-export-comerciais">
            <Download className="w-4 h-4" /> Exportar Excel
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <FilterSelect icon={Briefcase} label="Parceiro" value={partner} onChange={(v) => { setPartner(v); setComercial('all'); }} testid="lm-filter-partner"
            options={[{ v: 'all', l: 'Todos' }, ...partners.map((p) => ({ v: p.id, l: p.name }))]} />
          <FilterSelect icon={Users} label="Comercial" value={comercial} onChange={setComercial} testid="lm-filter-comercial"
            options={[{ v: 'all', l: 'Todos' }, { v: 'none', l: 'Por atribuir' }, ...usersOfPartner.map((u) => ({ v: u.id, l: u.name }))]} />
          <FilterSelect icon={CalendarRange} label="Período (entrada da lead)" value={period} onChange={setPeriod} testid="lm-filter-period"
            options={[{ v: 'all', l: 'Sempre' }, { v: 'hoje', l: 'Hoje' }, { v: 'semana', l: 'Esta semana' }, { v: 'mes', l: 'Este mês' }]} />
          <FilterSelect icon={Package} label="Lote importado" value={batch} onChange={setBatch} testid="lm-filter-batch"
            options={[{ v: 'all', l: 'Todos' }, ...(data?.batches || []).map((b) => ({ v: b.batch_id, l: b.name || `Importação ${fmtDT(b.batch_id)}` }))]} />
        </div>

        {!data ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-10 gap-3">
              {KPIS.map((x) => (
                <button key={x.key} type="button" onClick={() => open('Todas as leads', {}, x.key === 'total' ? '' : (x.sla || x.key))}
                  className={`text-left bg-white rounded-2xl border p-3 hover:shadow-md transition-shadow ${x.big ? 'border-2 ' + (x.key === 'convertida' ? 'border-emerald-300' : 'border-slate-300') : 'border-slate-200'}`}
                  data-testid={`lm-kpi-${x.key}`}>
                  <p className={`text-2xl font-bold ${x.cls}`}>{k[x.key] ?? 0}</p>
                  <p className="text-[11px] text-slate-500 leading-tight">{x.label}</p>
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 -mt-3" data-testid="lm-conv-pct">Taxa de conversão (leads atribuídas → contrato): <b className="text-emerald-700">{k.conv_pct ?? 0}%</b></p>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-200 p-4 overflow-x-auto" data-testid="lm-comerciais-table">
                <h3 className="text-sm font-bold text-slate-800 mb-1">Por comercial</h3>
                <p className="text-[11px] text-slate-400 mb-2">Clique no nome para ver as leads dele, por urgência.</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase text-slate-400 border-b">
                      <th className="py-2 pr-3">Comercial</th><th className="pr-3">Recebidas</th><th className="pr-3">Trabalhadas</th>
                      <th className="pr-3 text-emerald-700">Contratos</th><th className="pr-3">Sem contrato</th><th className="pr-3 text-rose-600">Paradas</th>
                      <th className="pr-3 text-orange-500">A expirar</th><th className="pr-3 text-rose-700">Expiradas</th><th className="pr-3">% Conv.</th><th>Média 1º contacto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.comerciais.map((r) => (
                      <tr key={r.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`lm-row-${r.user_id}`}>
                        <td className="py-2 pr-3">
                          <button type="button" onClick={() => open(`Leads de ${r.name}`, { comercial_id: r.user_id })} className="font-medium text-[#2D3E6F] hover:underline text-left" data-testid={`lm-open-comercial-${r.user_id}`}>{r.name}</button>
                          {r.partner_name && <p className="text-[10px] text-slate-400">{r.partner_name}</p>}
                        </td>
                        <td className="pr-3">{r.atribuidas}</td>
                        <td className="pr-3">{r.trabalhadas}</td>
                        <td className="pr-3 font-semibold text-emerald-700">{r.convertidas}</td>
                        <td className="pr-3 text-slate-500">{r.fechadas_sem_contrato}</td>
                        <td className="pr-3"><button type="button" onClick={() => open(`Leads de ${r.name}`, { comercial_id: r.user_id }, 'parada')} className={r.paradas ? 'text-rose-600 font-semibold underline' : 'text-slate-400'}>{r.paradas}</button></td>
                        <td className="pr-3"><button type="button" onClick={() => open(`Leads de ${r.name}`, { comercial_id: r.user_id }, 'a_expirar')} className={r.a_expirar ? 'text-orange-500 font-semibold underline' : 'text-slate-400'}>{r.a_expirar}</button></td>
                        <td className="pr-3"><button type="button" onClick={() => open(`Leads de ${r.name}`, { comercial_id: r.user_id }, 'expirada')} className={r.expiradas ? 'text-rose-700 font-semibold underline' : 'text-slate-400'} data-testid={`lm-expiradas-${r.user_id}`}>{r.expiradas}</button></td>
                        <td className="pr-3">{r.conv_pct}%</td>
                        <td className="text-xs text-slate-500">{r.avg_first_contact_h != null ? `${r.avg_first_contact_h}h` : '—'}</td>
                      </tr>
                    ))}
                    {!data.comerciais.length && <tr><td colSpan={10} className="py-8 text-center text-slate-400">Sem leads atribuídas neste filtro</td></tr>}
                  </tbody>
                </table>
              </div>
              <LmBatchesCard batches={data.batches} onSaved={load} onOpen={(b) => open(`Lote: ${b.name || fmtDT(b.batch_id)}`, { batch: b.batch_id })} />
            </div>
          </>
        )}
      </div>
      <LmLeadsSheet open={!!sheet} title={sheet?.title} filters={sheet?.filters || {}} initialSla={sheet?.sla} users={users}
        onClose={() => setSheet(null)} onChanged={load} />
    </Layout>
  );
}
